import axios from "../../../apis/Api";
import {
  GET_SCR_V2_REVERSIBLE_TRANSACTION_LIST,
  REOPEN_SCR_V2_TRANSACTION,
} from "../../../Types";
import { toast } from "react-toastify";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import { handleScrV2RequestError as handleRequestError } from "../SCRV2DetailReport/SCRV2RequestHelpers";
import {
  invalidateAllSCRV2ReportCaches,
  invalidateSCRV2CachesForScr,
} from "../SCRV2DetailReport/SCRV2DetailReportActions";

const isUsableScrId = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  const normalized = String(value).trim();
  return normalized.length > 0 && normalized.toLowerCase() !== "null" && normalized.toLowerCase() !== "undefined";
};

/**
 * Resolve the SCR entry id whose report caches must be invalidated after reopen.
 * Priority: reopen response ReversalSCRId → explicit UI scrId hint → legacy scrId fields.
 * Never treat scrNo as an SCR id.
 */
const resolveAffectedScrIdForInvalidation = (responsePayload, scrIdHint = null) => {
  const candidates = [
    responsePayload?.reversalSCRId,
    responsePayload?.ReversalSCRId,
    scrIdHint,
    responsePayload?.scrId,
    responsePayload?.SCRId,
  ];

  for (const candidate of candidates) {
    if (isUsableScrId(candidate)) {
      return String(candidate).trim();
    }
  }

  return null;
};

let reversibleListAbortController = null;
const transactionDetailCache = new Map();
const transactionDetailInFlight = new Map();

export const clearSCRTransactionDetailCache = (transactionId = null) => {
  if (!transactionId) {
    transactionDetailCache.clear();
    return;
  }

  [...transactionDetailCache.keys()].forEach((key) => {
    if (key.startsWith(`${transactionId}:`)) {
      transactionDetailCache.delete(key);
    }
  });
};

const normalizeDetailCategory = (transactionCategory) => {
  const category = (transactionCategory || "").trim();
  if (!category) {
    return "Non-VizLink";
  }

  if (category.toLowerCase() === "vizlink") {
    return "VizLink";
  }

  if (category.toLowerCase().includes("non-vizlink")) {
    return "Non-VizLink";
  }

  return category;
};

const buildTransactionDetailCacheKey = (transactionId, transactionCategory) =>
  `${transactionId}:${normalizeDetailCategory(transactionCategory)}`;

export const getSCRReversibleTransactionList =
  (request, { silent = false } = {}) =>
  async (dispatch) => {
  if (reversibleListAbortController) {
    reversibleListAbortController.abort();
  }

  reversibleListAbortController = new AbortController();
  const { signal } = reversibleListAbortController;

  if (!silent) {
    dispatch(showLoading());
  }

  try {
    const { data } = await axios.post(
      "api/SCR/SCROperations/transactions/reversible",
      request,
      { signal }
    );

    dispatch({
      type: GET_SCR_V2_REVERSIBLE_TRANSACTION_LIST,
      payload: data.response,
    });
  } catch (error) {
    handleRequestError(error);
  } finally {
    if (!signal.aborted && !silent) {
      dispatch(hideLoading());
    }
  }
};

export const peekSCRTransactionDetailCache = (transactionId, transactionCategory) => {
  const cacheKey = buildTransactionDetailCacheKey(transactionId, transactionCategory);
  return transactionDetailCache.get(cacheKey) || null;
};

export const prefetchSCRTransactionDetail = (transactionId, transactionCategory) => {
  if (!transactionId) {
    return Promise.resolve(null);
  }

  return getSCRTransactionDetail(transactionId, transactionCategory || "Non-VizLink").catch(
    () => null
  );
};

export const getSCRTransactionDetail = async (
  transactionId,
  transactionCategory,
  { useCache = true, forceRefresh = false } = {}
) => {
  const apiCategory = normalizeDetailCategory(transactionCategory);
  const cacheKey = buildTransactionDetailCacheKey(transactionId, apiCategory);

  if (useCache && !forceRefresh && transactionDetailCache.has(cacheKey)) {
    return { data: { response: transactionDetailCache.get(cacheKey) } };
  }

  const inFlightRequest = transactionDetailInFlight.get(cacheKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const fetchPromise = axios
    .get("api/SCR/SCROperations/transactions/detail", {
      params: {
        transactionId,
        transactionCategory: apiCategory,
      },
    })
    .then((response) => {
      if (response?.data?.response) {
        transactionDetailCache.set(cacheKey, response.data.response);
      }

      return response;
    })
    .finally(() => {
      transactionDetailInFlight.delete(cacheKey);
    });

  transactionDetailInFlight.set(cacheKey, fetchPromise);
  return fetchPromise;
};

export const reopenSCRTransaction = (transactionId, scrIdHint = null) => async (dispatch) => {
  try {
    const { data } = await axios.post(
      `api/SCR/SCROperations/non-vizlink-transactions/${transactionId}/reopen`
    );

    const succeeded =
      data?.isSuccess !== false &&
      data?.IsSuccess !== false &&
      data?.status !== "Error" &&
      data?.Status !== "Error";

    if (!succeeded) {
      toast.error(data?.message || data?.Message || "Transaction reversal failed.");
      return false;
    }

    const responsePayload = data?.response ?? data?.Response ?? null;
    const affectedScrId = resolveAffectedScrIdForInvalidation(responsePayload, scrIdHint);

    toast.success(data?.message || data?.Message || "Transaction reversed successfully.");
    clearSCRTransactionDetailCache(transactionId);
    if (affectedScrId) {
      invalidateSCRV2CachesForScr(affectedScrId);
    } else {
      // Safe fallback: reopen response should always include ReversalSCRId; if it does not,
      // clear all SCR V2 report caches rather than leave stale summary/detail/modal data.
      invalidateAllSCRV2ReportCaches();
    }
    dispatch({
      type: REOPEN_SCR_V2_TRANSACTION,
      payload: responsePayload,
    });
    return true;
  } catch (error) {
    handleRequestError(error);
    return false;
  }
};
